import { UnaryFunction } from 'rxjs/interfaces';

import {
  CategoryElement,
  ELEMENT_TYPE_CATEGORY,
  ELEMENT_TYPE_DATE,
  ELEMENT_TYPE_FILE,
  ELEMENT_TYPE_FORMATTED_TEXT,
  ELEMENT_TYPE_GROUP,
  ELEMENT_TYPE_IMAGE,
  ELEMENT_TYPE_LINK,
  ELEMENT_TYPE_LOCATION,
  ELEMENT_TYPE_NUMBER,
  ELEMENT_TYPE_OPTION_SELECTION,
  ELEMENT_TYPE_REFERENCE,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_TOGGLE,
  ELEMENT_TYPE_VIDEO,
  RenderingContext,
} from './../../api';
import { Generator } from './../generators/generator';
import { assertObject, forEach, forIn, objectAssign, reduceForIn } from './../js/js.utils';
import { identity, noop } from './../misc';
import { isArray, isFunction, isNil, isNotNil, isStringArray } from './../predicates/predicates';

const _ELEMENTS = 'elements';
const _VALUES = 'values';
const _VALUE = 'value';
const _ELEMENT_TYPE = 'elementType';

/**
 * Function that extracts a value
 */
declare type ValueExtractor<T> = UnaryFunction<any, T | T[] | null>;

/**
 * extractors
 */
const _EXTRACTORS: { [key: string]: ValueExtractor<any> } = {};

/**
 * Decodes a value into a date object
 *
 * @param aValue  the value
 * @return the object
 */
const _parseDate = (aValue: number | string | Date): Date => new Date(aValue as any);

// splits a category into its segments
const _parseCategory = (aCategory: string): string[] => aCategory.split('/');

/**
 * Parses a category element by adding a parsed value
 *
 * @param aCategory   the category
 * @return the augmented category
 */
function _parseCategories(aCategory: any): CategoryElement {
  // analyze
  const value = aCategory.categories;
  // augment
  aCategory.categoryPaths = isStringArray(value) ? value.map(_parseCategory) : [];
  // ok
  return aCategory;
}

/** Nothing to do for extraction */
const identityExtractor = identity;

/**
 * Extracts the 'values' field if it is an array, else the 'value' field
 *
 * @param aValue
 */
function valueValuesExtractor(aValue: any): any | any[] {
  let value;
  return isArray(value = aValue[_VALUES]) ? value : aValue[_VALUE];
}

/**
 * Extracts the 'values' field if it is an array, else the object itself
 *
 * @param aValue
 */
function valuesExtractor(aValue: any): any | any[] {
  let value;
  return isArray(value = aValue[_VALUES]) ? value : aValue;
}

/**
 * Converts a value
 */
declare type ValueConverter<T, R> = UnaryFunction<T, R>;

/**
 * Identity conversion
 */
const identityConverter = identity;

/**
 * Converts a value to a date object
 */
const dateConverter = _parseDate;

/**
 * Converts a category to a parsed category object
 */
const categoryConverter = _parseCategories;

/**
 * Converts a group element into a simpler structure
 */
const groupReducer = (res, el, name) => objectAssign(name, (_EXTRACTORS[el[_ELEMENT_TYPE]] || identity)(el), res);
const groupConverter = (aGroupElement: any): any => reduceForIn(aGroupElement, groupReducer, {});

/*
 * Assigns a single value property
 *
 * @param aName         key of the element
 * @param aValue        the value to assign
 * @param aSingleType   single value classifier
 * @param aMultiType    multi value classifier
 * @param aRenderingContext the rendering context
 */
function _setSingleValue<T>(aName: string, aValue: T, aSingleType: string, aMultiType: string, aRenderingContext: any) {
  // single element
  assertObject(aSingleType, aRenderingContext)[aName] = aValue;
  // multi value
  assertObject(aMultiType, aRenderingContext)[aName] = [aValue];
}

/*
 * Assigns a multi value property
 *
 * @param aName         key of the element
 * @param aValue        the value to assign
 * @param aSingleType   single value classifier
 * @param aMultiType    multi value classifier
 * @param aRenderingContext the rendering context
 */
function _setMultiValue<T>(aName: string, aValue: T[], aSingleType: string, aMultiType: string, aRenderingContext: any) {
  // single element
  if (aValue.length > 0) {
    assertObject(aSingleType, aRenderingContext)[aName] = aValue[0];
  }
  // multi value
  assertObject(aMultiType, aRenderingContext)[aName] = aValue;
}

/**
 * Returns a combined function that extracts a value and converts it
 *
 * @param aExtractor  the extractor function
 * @param aConverter  the converter
 *
 * @return the function
 */
function _extractAndConvert<T, R>(aExtractor: ValueExtractor<T>, aConverter: ValueConverter<T, R>): ValueExtractor<R> {
  // returns the conversion function
  return aValue => {
    // analyze
    const extracted: T | T[] = aExtractor(aValue);
    return isArray(extracted) ? ((aConverter === identityConverter) ? (extracted as any as R[]) : extracted.map(aConverter)) : isNotNil(extracted) ? aConverter(extracted) : null;
  };
}

/*
 * Assigns the value object for elements that have their single value property represented
 * as 'value' and multi value property as 'values'.
 */
function _handleElement<T, R>(aSingleType: string, aMultiType: string, aName: string, aElement: any, aRenderingContext: any, aExtractor: ValueExtractor<T>) {
  // analyze
  const extracted: T | T[] = aExtractor(aElement);
  // check
  if (isArray(extracted)) {
    // multi values
    _setMultiValue(aName, extracted, aSingleType, aMultiType, aRenderingContext);
  } else
    if (isNotNil(extracted)) {
      // single value
      _setSingleValue(aName, extracted, aSingleType, aMultiType, aRenderingContext);
    }
}

declare type HandlerType = (aName: string, aElement: any, aRenderingContext: any) => void;

/*
 * Very simple way to convert the value into a plural form
 *
 * @param aValue    the value
 * @return the plural form of the value
 */
function _pluralize(aValue: string): string {
  return aValue + 's';
}

/*
 * Handlers for element types that copy the actual element data to a strongly typed location
 * into the rendering context.
 */
const _HANDLERS: { [key: string]: HandlerType } = {};

enum HandlerKind {
  SINGLE_VALUE = 1,
  MULTI_VALUES,
  SINGLE
}

/**
 * Records the keys of the registered handlers
 */
const _HANDLER_KEYS: { [key: string]: HandlerKind } = {};

/*
 * Registers the values element by type ID, plural is formed by appending an 's'.
 *
 * @param aType     the type
 */
function _valueValuesElement<T, R>(aType: string, aConverter: ValueConverter<T, R>) {
  // fields
  const types = _pluralize(aType);
  const extractor = _extractAndConvert(valueValuesExtractor, aConverter);
  // returns the handler
  const handler = (aName: string, aElement: any, aRenderingContext: any) =>
    _handleElement(aType, types, aName, aElement, aRenderingContext, extractor);
  // register
  _HANDLERS[aType] = handler;
  // register the kind
  _HANDLER_KEYS[aType] = HandlerKind.SINGLE_VALUE;
  _HANDLER_KEYS[types] = HandlerKind.MULTI_VALUES;
  // register the typed extractor
  _EXTRACTORS[aType] = extractor;
}

/*
 * Registers the values element by type ID, plural is formed by appending an 's'.
 *
 * @param aType     the type
 */
function _valuesElement<T, R>(aType: string, aConverter: ValueConverter<T, R>) {
  // fields
  const types = _pluralize(aType);
  const extractor = _extractAndConvert(valuesExtractor, aConverter);
  // returns the handler
  const handler = (aName: string, aElement: any, aRenderingContext: any) =>
    _handleElement(aType, types, aName, aElement, aRenderingContext, extractor);
  // register
  _HANDLERS[aType] = handler;
  // register the kind
  _HANDLER_KEYS[aType] = HandlerKind.SINGLE;
  _HANDLER_KEYS[types] = HandlerKind.MULTI_VALUES;
  // register the typed extractor
  _EXTRACTORS[aType] = extractor;
}

/*
 * Registers all types in the array
 *
 * @param aTypes    the types
 */
const _valueValuesElements = (aTypes: string[]) => forEach(aTypes, type => _valueValuesElement(type, identityConverter));

/*
 * Registers all types in the array
 *
 * @param aTypes    the types
 */
const _valuesElements = (aTypes: string[]) => forEach(aTypes, type => _valuesElement(type, identityConverter));

/*
 * Registers the date group element handler. This one is special, because it flattens the data structure
 */
const _groupElement = () => _valueValuesElement(ELEMENT_TYPE_GROUP, groupConverter);

/*
 * Registers the date element handler. This one is special, because it converts
 * the date strings into date objects.
 */
const _dateElement = () => _valueValuesElement(ELEMENT_TYPE_DATE, dateConverter);

/*
 * Registers the category element handler. This one is special, because it splits categories
 * into paths.
 */
function _categoryElement() {
  // plural
  const type = ELEMENT_TYPE_CATEGORY;
  // the extractors
  const extractor = _extractAndConvert(valuesExtractor, categoryConverter);
  // returns the handler
  const handler = (aName: string, aElement: CategoryElement, aRenderingContext: any) => assertObject(type, aRenderingContext)[aName] = extractor(aElement);
  // register
  _HANDLERS[type] = handler;
  // register the kind
  _HANDLER_KEYS[type] = HandlerKind.SINGLE;
  // register the typed extractor
  _EXTRACTORS[type] = extractor;
}

const _ignoreElement = noop;

// register the types that use 'value' for single and 'values' for multi elements
_valueValuesElements([
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_FORMATTED_TEXT,
  ELEMENT_TYPE_NUMBER,
  ELEMENT_TYPE_OPTION_SELECTION,
  ELEMENT_TYPE_TOGGLE,
  ELEMENT_TYPE_REFERENCE
]);
// special case handling for groups
_groupElement();
// special case handling for dates
_dateElement();
// special case handling for categories
_categoryElement();
// register the types that have inline values
_valuesElements([
  ELEMENT_TYPE_FILE,
  ELEMENT_TYPE_LINK,
  ELEMENT_TYPE_IMAGE,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_LOCATION
]);

/*
 * Adds typings next to the elements
 *
 * @param aRenderingContext     the rendering context to modify
 * @return the context
 */
function _addTypings(aRenderingContext: RenderingContext): RenderingContext {
  // iterate
  forIn(aRenderingContext[_ELEMENTS], (element, name) => (_HANDLERS[element[_ELEMENT_TYPE]] || _ignoreElement)(name, element, aRenderingContext));
  // returns the context
  return aRenderingContext;
}

/**
 * Decodes the expression back to an expression into elements
 *
 * @param aExpression   the original expression
 * @return the decoded expression
 */
function _decodeExpression(aExpression: string): string {
  // check for the first dot
  const idx = aExpression.indexOf('.');
  if (idx >= 0) {
    // check if we have a mapping
    const key = aExpression.substr(0, idx);
    const kind = _HANDLER_KEYS[key];
    if (kind) {
      // check the element name
      const idx1 = aExpression.indexOf('.', idx + 1);
      const name = (idx1 > idx) ? aExpression.substring(idx + 1, idx1) : aExpression.substr(idx + 1);
      // extract the name
      switch (kind) {
        case HandlerKind.SINGLE_VALUE:
          return `${_ELEMENTS}.${name}.${_VALUE}`;
        case HandlerKind.MULTI_VALUES:
          return `${_ELEMENTS}.${name}.${_VALUES}`;
        case HandlerKind.SINGLE:
          return `${_ELEMENTS}.${name}`;
      }
    }
  }
  // just as is
  return aExpression;
}


// can't use a symbol since we use this aross libraries
const KEY_EXPRESSION = '45b01348-de92-44a0-8103-7b7dc471ad8c';

// gets an expression
declare type ExpressionGetter = Generator<string>;

/**
 * Returns the property binding expression from the getter of a property. A property is always bound to
 * a top level element, NOT to an inner value of a group expression.
 *
 * @param aPrototype        the prototype object
 * @param aPropertyKey      the property key
 *
 * @return the expression or undefined if it does not exist
 */
function _getExpressionFromProperty(aPrototype: any, aPropertyKey: string): string | null | undefined {
  // check if we have a property descriptor
  let obj = aPrototype;
  while (obj) {
    // access the descriptor
    const desc: PropertyDescriptor = Object.getOwnPropertyDescriptor(obj, aPropertyKey);
    if (desc) {
      // check if we have a getter
      const getter = desc.get;
      if (isFunction(getter)) {
        // access the expression
        const expGetter = getter[KEY_EXPRESSION] as ExpressionGetter;
        if (isFunction(expGetter)) {
          // compute it
          return expGetter();
        }
      }
    }
    // walk up
    obj = Object.getPrototypeOf(obj);
  }
  // nothing
  return undefined;
}

// matches any valid expression
const PROPERTY_REGEX = /^([^\[]+)(\[\d+\])?$/;

/**
* Tranlates the accessor to an expression
*
* @param aPrototype the object
* @param aAccessor the accessor string, possibly indexed
*
* @return  the accessor expression, relative to the rendering context
*/
function _getExpressionFromAccessor(aPrototype: any, aAccessor: string): string | undefined {
  // split the accessor
  const tokens: string[] = aAccessor.trim().split('.');
  // check if the
  if (tokens.length > 0) {
    // resolve the property of the first token
    const [firstTotal, firstKey, firstIndex] = PROPERTY_REGEX.exec(tokens[0]);
    if (firstKey === _ELEMENTS) {
      // this is an expression directly into the elements structure, use as is
      return aAccessor;
    }
    // the root expression
    const expression: string[] = [_getExpressionFromProperty(aPrototype, firstKey) || firstKey];
    if (isNotNil(firstIndex)) {
      expression.push(firstIndex);
    }
    // resolve the remaining items
    const len = tokens.length;
    let idx = 1;
    while (idx < len) {
      // split
      const [total, key, index] = PROPERTY_REGEX.exec(tokens[idx++]);
      // test the index signature
      if (isNotNil(index)) {
        // append
        expression.push('.', key, '.', _VALUES, index);
      } else {
        expression.push('.', key, '.', _VALUE);
      }
    }
    // ok
    return expression.join('');
  }
  // nothing special to return
  return aAccessor;
}

export {
  _addTypings as wchAddTypings,
  _decodeExpression as wchDecodeExpression,
  _getExpressionFromAccessor as wchDecodeAccessor
};
